import { useCallback, useSyncExternalStore } from 'react';

// Admin UI (.admin-ui) の一括表示/非表示を管理するトグル。
// 状態は localStorage に保持し、<html> のクラスと注入 CSS を連動させる。
const STORAGE_KEY = '@rt/hide-admin-ui';
const HTML_CLASS = 'hide-admin-ui';
const STYLE_ELEMENT_ID = 'rt-hide-admin-ui-style';

// .admin-ui を非表示にする CSS を一度だけ head に注入する。
const ensureStyle = () => {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = `.${HTML_CLASS} .admin-ui{display:none !important}`;
  document.head.appendChild(style);
};

const readStored = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const applyHidden = (hidden: boolean) => {
  ensureStyle();
  document.documentElement.classList.toggle(HTML_CLASS, hidden);
  try {
    if (hidden) {
      localStorage.setItem(STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage が使えなくても表示制御は継続する
  }
};

// 各呼び出し元が独立した useState を持つと、あるコンポーネントでのトグルが同一タブ内の
// 他コンポーネント（タブ除外判定など）に伝わらない。全インスタンスで1つの値を共有する
// 外部ストアにし、変更をリスナーへ通知する。
let currentHidden = typeof document !== 'undefined' ? readStored() : false;
const listeners = new Set<() => void>();

const setHidden = (hidden: boolean) => {
  if (hidden === currentHidden) return;
  currentHidden = hidden;
  applyHidden(hidden);
  for (const listener of listeners) listener();
};

// マウント前に即時反映し、リロード直後に admin UI が一瞬露出するのを防ぐ。
if (typeof document !== 'undefined') {
  applyHidden(currentHidden);
}

// 他タブでの切り替えにも追従させる。
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    setHidden(readStored());
  });
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => currentHidden;
const getServerSnapshot = () => false;

export const useHideAdminUi = () => {
  const hidden = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const toggle = useCallback(() => setHidden(!currentHidden), []);

  return { hidden, toggle };
};
